import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { useAppDispatch } from 'app/store';
import CustomCheckbox from 'components/shared/forms/CustomCheckbox';
import { IPageTab } from 'components/shared/PageTabs/PageTabs';
import SearchInput from 'components/shared/forms/SearchForm/SearchInput';
import PageTabs from 'components/shared/PageTabs';
import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';
import { projectsFilterListSelector } from 'features/projectsFilterList/projectsFilterListSlice';
import { permissionsUserSelector } from 'features/user/userSlice';
import { getProjectsFilterList } from 'features/projectsFilterList/projectsFilterListActions';
import { IFetchMembersParams } from 'features/members/membersTypes';
import { getMembers } from 'features/members/membersActions';
import {
  filterMembersSelector,
  setMembersPageFilter,
} from 'features/members/membersSlice';
import { startsWithNoSpace } from 'helpers/regularExpressions';
import styles from './Members.module.scss';
import { createInvitations, getInvitations } from 'features/invitationLinksList/invitationLinksListActions';
import {
  filterInvitationsSelector,
  setInvitationLinksPageFilter
} from 'features/invitationLinksList/invitationLinksListSlice';
import { IFetchInvitationsParams } from 'features/invitationLinksList/invitationLinksListTypes';
import EmailUserRoleInvitationsForm from 'components/members/EmailUserRoleInvitationsForm';

const Members: React.FC = () => {
  const membersPageFilter = useSelector(filterMembersSelector);
  const invitationLinksPageFilter = useSelector(filterInvitationsSelector);
  const permissions = useSelector(permissionsUserSelector);
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isInvitationsTabActive = pathname.endsWith('/invitations');
  const [emailInvitationFormOpen, setEmailInvitationFormOpen] = useState(false);
  const didMountRef = useRef(false);
  const tabsData: IPageTab[] = [
    {
      path: `/members`,
      name: 'Active members',
    },
    {
      path: `invitations`,
      name: 'Invitations',
    },
  ];

  const toggleShowDeletedMembers = () => {
    dispatch(
      setMembersPageFilter({
        ...membersPageFilter,
        showDeleted: !membersPageFilter.showDeleted,
      })
    );
  };
  const handleMemberSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (startsWithNoSpace.test(text)) {
      dispatch(
        setMembersPageFilter({
          ...membersPageFilter,
          searchValue: text,
          skip: 0,
          take: 20,
        })
      );
    }
  };

  const toggleShowUsedInvitations = () => {
    dispatch(
      setInvitationLinksPageFilter({
        ...invitationLinksPageFilter,
        showAccepted: !invitationLinksPageFilter.showAccepted,
      })
    );
  };
  const handleInvitationMemberSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (startsWithNoSpace.test(text)) {
      dispatch(
        setInvitationLinksPageFilter({
          ...invitationLinksPageFilter,
          search: text,
          skip: 0,
          take: 20,
        })
      );
    }
  };

  const clearMembersFilter = () => {
    dispatch(
      setMembersPageFilter({
        ...membersPageFilter,
        searchValue: '',
        skip: 0,
        take: 20,
      })
    );
  };
  const clearInvitationsFilter = () => {
    dispatch(
      setInvitationLinksPageFilter({
        ...invitationLinksPageFilter,
        search: '',
        skip: 0,
        take: 20,
      })
    );
  };

  const debouncedMembersFiltration = useCallback(
    debounce((params: IFetchMembersParams) => {
      dispatch(getMembers(params));
    }, 300),
    []
  );
  const debouncedInvitationsFiltration = useCallback(
    debounce((params: IFetchInvitationsParams) => {
      dispatch(getInvitations(params));
    }, 300),
    []
  );

  let tabsWithPermissions: IPageTab[] = tabsData;
  const isInvitesAvailable = permissions?.some(
    (el) => el.name === 'ViewInvitationLinksPage'
  );

  if (!isInvitesAvailable) {
    tabsWithPermissions = tabsData.filter((el) => el.name !== 'Invitation Links');
  }

  useEffect(() => {
    dispatch(getMembers(membersPageFilter));
    dispatch(getInvitations(invitationLinksPageFilter));
    dispatch(getProjectsFilterList());
  }, []);

  useEffect(() => {
    if (didMountRef.current) {
      debouncedMembersFiltration(membersPageFilter);
    }
    didMountRef.current = true;
  }, [membersPageFilter.searchValue, membersPageFilter.showDeleted]);
  useEffect(() => {
    if (didMountRef.current) {
      debouncedInvitationsFiltration(invitationLinksPageFilter);
    }
    didMountRef.current = true;
  }, [invitationLinksPageFilter.search, invitationLinksPageFilter.showAccepted]);

  return (
    <div className={`${styles.flexColumn} ${styles.hFull}`}>
      <div className={styles.controls}>
        <PageTabs tabsData={tabsWithPermissions} />
        <div className={styles.searchAndActionBtnWrapper}>
          {!isInvitationsTabActive ? (
            <>
              <div className={styles.checkboxWrapper}>
                <CustomCheckbox
                  label="Show removed members"
                  isChecked={membersPageFilter.showDeleted}
                  handleChange={toggleShowDeletedMembers}
                />
              </div>
              <div className={styles.searchWrapper}>
                <SearchInput
                  value={membersPageFilter.searchValue}
                  onChange={handleMemberSearchInputChange}
                  clearInput={clearMembersFilter}
                  placeholder="Search members"
                  outlined
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.checkboxWrapper}>
                <CustomCheckbox
                  label="Show accepted invitations"
                  isChecked={invitationLinksPageFilter.showAccepted}
                  handleChange={toggleShowUsedInvitations}
                />
              </div>
              <div className={styles.searchWrapper}>
                <SearchInput
                  value={invitationLinksPageFilter.search}
                  onChange={handleInvitationMemberSearchInputChange}
                  clearInput={clearInvitationsFilter}
                  placeholder="Search accepters"
                  outlined
                />
              </div>
              <Button
                variant="primary"
                onClick={() => setEmailInvitationFormOpen(true)}
                aria-label="Open email invitation form."
                style={{ width: 200 }}
              >
                Add invitations
              </Button>

              <Modal
                title="Add new invitations"
                open={emailInvitationFormOpen}
                onClose={() => setEmailInvitationFormOpen(false)}
              >
                <EmailUserRoleInvitationsForm
                  closeInvitationForm={() => setEmailInvitationFormOpen(false)}
                  onSubmit={invitations => {
                    dispatch(createInvitations(invitations));
                    setEmailInvitationFormOpen(false)
                  }}
                  // organizationProjects={projects}
                />
              </Modal>
            </>
          )}
        </div>
      </div>

      <Outlet />
    </div>
  );
};

export default Members;
